import { makeAutoObservable } from "mobx";
import {
  ColumnSourceEnum,
  ColumnToDisplay,
  DynamicColumnOUT,
  ObjectListIN,
  ObjectListOUT,
  PropertyBrowserTreeNode,
} from "../../api-client";
import {
  ColumnBrowserOpen as ColumnSettings,
  ColumnVisibilityState,
} from "../MappingEditor/types";
import { OBJECT_COUNT_ID } from "./consts";
import { inventoryStore } from "features/Inventory/InventoryStore";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import {
  filterInventoryStore,
  filterMEStore,
} from "./features/Filters/FiltersStore";
import { FilterDefaultGrouping } from "./features/Filters/Filters.tsx";

interface propertiesDictionary {
  [id: string]: PropertyBrowserTreeNode;
}

export const TableMode = {
  Inventory: "Inventory",
  MappingEditor: "DME",
  None: "None",
} as const;

type TableModeTypeof = typeof TableMode;

export type TableModeType = TableModeTypeof[keyof typeof TableMode];

const PAGE_SIZE = 100;

export class DynamicTableStore {
  mode: TableModeType = TableMode.None;
  dataFetchLoading: boolean = false;
  data: ObjectListOUT = {
    items: [],
    count: 0,
  };
  dynamicColumns: DynamicColumnOUT[] = [];
  visibleColumnProperties: PropertyBrowserTreeNode[] = [];
  openColumnSettings: ColumnSettings = {
    groupName: "",
    open: false,
    positionX: 0,
    positionY: 0,
  };
  properties: PropertyBrowserTreeNode[] = [];
  propertiesDictionary: propertiesDictionary = {};
  columnsVisibilityState: ColumnVisibilityState = {};
  columnsOrder: string[] = [];
  errorFetchingObjects: boolean = false;
  openAddColumns: boolean = false;

  get preferredStore() {
    return this.mode === TableMode.Inventory ? inventoryStore : MEStore;
  }

  get filterStore() {
    return this.mode === TableMode.Inventory
      ? filterInventoryStore
      : filterMEStore;
  }

  setMode(mode: TableModeType) {
    this.mode = mode;
  }

  setData(data: ObjectListOUT) {
    this.data = data;
  }

  setDynamicColumns(dynamicColumns: DynamicColumnOUT[]) {
    this.dynamicColumns = dynamicColumns;
  }

  setDataFetchLoading(dataFetchLoading: boolean) {
    this.dataFetchLoading = dataFetchLoading;
  }

  setOpenColumnSettings(openColumnSettings: ColumnSettings) {
    this.openColumnSettings = openColumnSettings;
  }

  setVisibleColumnProperties(properties: PropertyBrowserTreeNode[]) {
    this.visibleColumnProperties = properties;
  }

  setProperties(properties: PropertyBrowserTreeNode[]) {
    this.properties = properties;
  }

  setPropertiesDictionary(propertiesDictionary: propertiesDictionary) {
    this.propertiesDictionary = propertiesDictionary;
  }

  setColumnsVisibilityState(columnsState: ColumnVisibilityState) {
    this.columnsVisibilityState = columnsState;
  }

  setColumnsOrder(columnsOrder: string[]) {
    this.columnsOrder = columnsOrder;
  }

  setErrorFetchingObjects(errorFetchingObjects: boolean) {
    this.errorFetchingObjects = errorFetchingObjects;
  }

  setOpenAddColumns(openAddColumns: boolean) {
    this.openAddColumns = openAddColumns;
  }

  async resetConfiguration() {
    const defaultKeys = this.getDBSavedDefaultColumnsKeys();
    this.setDefaultVisibleColumnProperties(defaultKeys);
    this.filterStore.reset();

    const nameProperty = this.visibleColumnProperties.find(
      (item) => item.column_key === "physical_object.component"
    );
    if (nameProperty) {
      this.filterStore.setSelectedSortedByItems([
        {
          id: String(0),
          property: nameProperty,
          sort: "ASC",
        },
      ]);
    }

    localStorage.removeItem(`${this.mode}_columns_order`);
    localStorage.removeItem(`${this.mode}_filter`);
    localStorage.removeItem(`${this.mode}_sort`);
    localStorage.removeItem(`${this.mode}_groupby`);
    await this.refreshTableObjects();
  }

  get tableColumnsOrder(): string[] {
    return ["select", OBJECT_COUNT_ID, ...this.columnsOrder];
  }

  getColumnsProps() {
    return this.visibleColumnProperties.map((item) => ({
      column_key: item.column_key,
      column_source: item.column_source,
    })) as ColumnToDisplay[];
  }

  getAllPropertiesFlat() {
    const allProperties: { [id: string]: PropertyBrowserTreeNode } = {};

    const addColumnKey = (item: PropertyBrowserTreeNode) => {
      if (item.column_key && item.column_key !== "") {
        allProperties[item.column_key] = item;
      }
      item.children?.forEach(addColumnKey);
    };

    this.properties.forEach(addColumnKey);
    this.setPropertiesDictionary(allProperties);
  }

  findPropertyByKey(column_key: string | undefined) {
    if (!column_key) return;
    return this.propertiesDictionary[column_key];
  }

  getDBSavedDefaultColumnsKeys() {
    const defaultColumnsKeys = this.dynamicColumns
      .filter((item) => item.default)
      .map((item) => item.key_name);
    return defaultColumnsKeys;
  }

  setDefaultVisibleColumnProperties(visibleColumnNames: string[] | null) {
    const visibleColumns = (visibleColumnNames ?? [])
      .map((key) => this.propertiesDictionary[key])
      .filter(Boolean);
    this.setVisibleColumnProperties(visibleColumns);
  }

  filterPropertiesByAvailableColumns() {
    const visibleKeys = new Set(
      this.visibleColumnProperties.map((item) => item.column_key)
    );

    const filterNode = (
      node: PropertyBrowserTreeNode
    ): PropertyBrowserTreeNode | null => {
      const hasChildren = node.children && node.children.length > 0;

      if (!hasChildren) {
        return visibleKeys.has(node.column_key) ? { ...node } : null;
      }

      const filteredChildren = (node.children ?? [])
        .map(filterNode)
        .filter((child): child is PropertyBrowserTreeNode => child !== null);

      if (filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }

      return null;
    };

    return this.properties
      .map(filterNode)
      .filter((node): node is PropertyBrowserTreeNode => node !== null);
  }

  filterNotSelectedItems(
    data: PropertyBrowserTreeNode[],
    selectedItems: PropertyBrowserTreeNode[]
  ) {
    // Make a copy of the tree and remove objects that exists in selectedItems.
    // Remove parents that originally had children and after filtering they don't
    const toRemove: Set<string> = new Set(
      selectedItems.map((item) => item.column_key)
    );

    const removeFiltered = (
      original: PropertyBrowserTreeNode
    ): PropertyBrowserTreeNode | null => {
      if (toRemove.has(original.column_key)) {
        return null;
      }

      const updated = {
        ...original,
        children: (original.children ?? [])
          .map(removeFiltered)
          .filter((x) => x != null),
      } as PropertyBrowserTreeNode;

      if (original.children?.length && updated.children?.length === 0) {
        return null;
      }

      return updated;
    };

    return data
      .map(removeFiltered)
      .filter((x) => x != null) as PropertyBrowserTreeNode[];
  }

  updateData(receivedData: ObjectListOUT, update?: boolean) {
    if (receivedData && !receivedData.items) return;
    if (update && receivedData) {
      this.setData(receivedData);
    } else if (!update && receivedData) {
      this.setData({
        items: [...this.data.items, ...receivedData.items],
        count: receivedData.count,
      });
    }
  }

  async refreshTableObjects() {
    await this.preferredStore.refreshTableObjects();
  }

  // API calls
  async getProperties() {
    const response = await this.preferredStore.getProperties();
    if (response) {
      this.setProperties(response);
      this.getAllPropertiesFlat();
    }
  }

  async fetchColumns(): Promise<DynamicColumnOUT[] | void> {
    const response = await this.preferredStore.fetchColumns();
    this.setDynamicColumns(response ?? []);
  }

  async fetchColumnSuggestions(property: PropertyBrowserTreeNode) {
    return this.preferredStore.fetchColumnSuggestions(property);
  }

  async fetchObjectsList(start: number, update?: boolean) {
    if (this.dataFetchLoading) return;
    this.setDataFetchLoading(true);
    this.setErrorFetchingObjects(false);

    const props = {
      columns_to_display: this.getColumnsProps(),
    } as ObjectListIN;
    if (this.filterStore.validFilterRules) {
      props.filtering = this.filterStore.validFilterRules;
    }
    if (this.filterStore.validSortingRules) {
      props.sorting = this.filterStore.validSortingRules;
    }
    if (this.filterStore.validGroupByRules.length) {
      props.grouping = this.filterStore.validGroupByRules;
    }

    props.one_row_per_object =
      this.filterStore.defaultGrouping == FilterDefaultGrouping.Object;
    const isMappingEditorMode = this.mode == TableMode.MappingEditor;
    props.include_matches = isMappingEditorMode;
    props.include_ifc_id = isMappingEditorMode && !props.grouping;

    const page = Math.round(start / PAGE_SIZE) + 1;

    const response = await this.preferredStore.fetchObjectsList(
      props,
      page,
      PAGE_SIZE
    );
    if (response) {
      this.updateData(response, update);
    }
    this.setDataFetchLoading(false);
  }

  async triggerExport() {
    const props = {
      columns_to_display: this.getColumnsProps(),
    } as ObjectListIN;
    if (this.filterStore.validFilterRules) {
      props.filtering = this.filterStore.validFilterRules;
    }
    if (this.filterStore.validSortingRules) {
      props.sorting = this.filterStore.validSortingRules;
    }
    if (this.filterStore.validGroupByRules.length) {
      props.grouping = this.filterStore.validGroupByRules;
    }
    props.one_row_per_object =
      this.filterStore.defaultGrouping == FilterDefaultGrouping.Object;

    return await inventoryStore.triggerExport(props);
  }

  resetTableData() {
    this.data = {
      items: [],
      count: 0,
    };
    this.properties = [];
    this.propertiesDictionary = {};
    this.visibleColumnProperties = [];
    this.dynamicColumns = [];
    this.openColumnSettings.open = false;
    this.openAddColumns = false;
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: DynamicTableStore;

  static getInstance(): DynamicTableStore {
    if (!DynamicTableStore.instance) {
      DynamicTableStore.instance = new DynamicTableStore();
    }
    return DynamicTableStore.instance;
  }
}

export const dynamicTableStore = DynamicTableStore.getInstance();
